import React, { useEffect } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Box, TextField, Stack } from '@mui/material';
import { Select, MultiSelect, FormField } from '@shared/components';
import FieldGroup from '../AddEditOffer/FieldGroup';
import { OFFER_FIELD_TOOLTIPS } from '../../constants/fieldTooltips';
import { IDropdownOptions } from '@shared/interface';

interface OfferDetailsSectionProps {
  control: Control<any>;
  errors: FieldErrors;
  isEditing: boolean;
  scopeOptions: IDropdownOptions[];
  categoryOptions: IDropdownOptions[];
  currencyOptions: IDropdownOptions[];
  priceOptions: IDropdownOptions[];
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
}

export const OfferDetailsSection: React.FC<OfferDetailsSectionProps> = ({
  control,
  errors,
  isEditing,
  scopeOptions,
  categoryOptions,
  currencyOptions,
  priceOptions,
  watch,
  setValue,
}) => {
  // Categories that should show the duration field
  const CATEGORIES_REQUIRE_DURATION: number[] = categoryOptions
    .filter((cat) => /\bPremium Account\b/i.test(cat.label))
    .map((cat) => cat.optionId)
    .filter(Boolean);

  const selectedCategory = watch('offer_category');
  const showDuration: boolean = !!selectedCategory && CATEGORIES_REQUIRE_DURATION.includes(selectedCategory);

  useEffect(() => {
    if (!showDuration) {
      setValue('duration', 0, { shouldValidate: true, shouldDirty: true });
    }
  }, [showDuration, setValue]);

  return (
    <Stack spacing={2}>
      {/* Name and Platform - Side by Side */}
      <FieldGroup>
        <Box sx={{ flex: 1 }}>
          <FormField label="Offer Name" required tooltip={OFFER_FIELD_TOOLTIPS.offer_name} />
          <Controller
            name="offer_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                fullWidth
                error={!!errors.offer_name}
                helperText={(errors.offer_name?.message as string) || ' '}
                data-testid="offer-name-input"
                placeholder="e.g., Premium 30 Days Special"
              />
            )}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <FormField label="Platform (Scope)" required tooltip={OFFER_FIELD_TOOLTIPS.scope} />
          <Controller
            name="scope"
            control={control}
            render={({ field }) => {
              return isEditing ? (
                <Select
                  value={Array.isArray(field.value) ? field.value[0] ?? '' : field.value}
                  onChange={(value) => field.onChange(value ? [value] : [])}
                  options={scopeOptions}
                  error={errors.scope?.message as string}
                  data-testid="scope-select"
                  useOptionId
                  placeholder="Select platform"
                />
              ) : (
                <MultiSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={scopeOptions}
                  error={errors.scope?.message as string}
                  data-testid="scope-select"
                  useOptionId
                  placeholder="Select platforms"
                />
              );
            }}
          />
        </Box>
      </FieldGroup>

      {/* Category */}
      <FieldGroup>
        <Box sx={{ flex: 1 }}>
          <FormField label="Category" required tooltip={OFFER_FIELD_TOOLTIPS.offer_category} />
          <Controller
            name="offer_category"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                options={categoryOptions}
                error={errors.offer_category?.message as string}
                errorHeight
                data-testid="category-select"
                useOptionId
                placeholder="Select category"
              />
            )}
          />
        </Box>
        {/* Duration - Full Width (only for Premium Account) */}
        {/* {showDuration && ( */}
        <Box sx={{ flex: 1 }}>
          <FormField label="Duration (seconds)" tooltip={OFFER_FIELD_TOOLTIPS.duration} />
          <Controller
            name="duration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                type="number"
                fullWidth
                error={!!errors.duration}
                helperText={(errors.duration?.message as string) || ' '}
                data-testid="duration-input"
                placeholder="86400"
              />
            )}
          />
        </Box>
        {/* )} */}
        <Box sx={{ flex: 1 }}>
          <FormField label="Quantity" tooltip={OFFER_FIELD_TOOLTIPS.quantity} />
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                type="number"
                fullWidth
                error={!!errors.quantity}
                helperText={(errors.quantity?.message as string) || ''}
                data-testid="quantity-input"
                placeholder="0"
              />
            )}
          />
        </Box>
      </FieldGroup>

      {/* Price and Currency - Side by Side */}
      <FieldGroup>
        <Box sx={{ flex: 1 }}>
          <FormField label="Price" required tooltip={OFFER_FIELD_TOOLTIPS.price} />
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                options={priceOptions}
                error={errors.price?.message as string}
                data-testid="price-select"
                useOptionId
                placeholder="Select price"
              />
            )}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <FormField label="Currency" required />
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                options={currencyOptions}
                error={errors.currency?.message as string}
                data-testid="currency-select"
                useOptionId
                placeholder="Select currency"
              />
            )}
          />
        </Box>
      </FieldGroup>
      <Box sx={{ flex: 1 }}>
        <FormField label="Internal Comment" tooltip={OFFER_FIELD_TOOLTIPS.comment} />
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              multiline
              rows={3}
              fullWidth
              error={!!errors.comment}
              helperText={(errors.comment?.message as string) || ' '}
              data-testid="comment-input"
              placeholder="Add internal notes here..."
            />
          )}
        />
      </Box>
    </Stack>
  );
};
